<script lang="ts">
  /**
   * 問題の作業台の 1 区画（problem-format.md §7）: エディタ → 実行（標準入力 1 つ）と採点（全ケース）→
   * 段階ヒント 3 段 → 模範解答。例題と類題で同じ部品を使い、data-testid の接頭辞だけを変える。
   * - 初期値は書きかけ（§8）があればそれ、無ければ starter。編集が止まって 1 秒後に保存する。
   * - 採点の結果・ヒントを開いた数は保存しない（再読み込みで消える）。
   * - 模範解答は「採点して AC」か「ヒント 3 を開いた後に『模範解答を見る』」でだけ出す。
   */
  import { onDestroy } from 'svelte';
  import type { RunResponse } from '../../lib/sandboxd';
  import type { JudgeResult } from '../../lib/judge';
  import CodeBlock from '../CodeBlock.svelte';
  import CodeEditor from '../CodeEditor.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import { RunState } from '../cards/run-state.svelte';
  import RunStatus from '../cards/RunStatus.svelte';
  import { caseLabel, compileErrorLine, firstFailure, writeAcResult } from '../cards/write-ac';
  import { applyTimeLimit, overLimit } from './time-limit';
  import { gradeProblemCode, runProblemCode, type ProblemRunTarget } from './problem-run';
  import type { DraftStore, HintBlock, ProblemCase } from './types';

  interface Props {
    /** data-testid の接頭辞（例題 problem-*、類題 exercise-*） */
    kind: 'problem' | 'exercise';
    target: ProblemRunTarget;
    draftKey: string;
    starter: string;
    cases: readonly ProblemCase[];
    solution: string;
    hints: readonly HintBlock[];
    /** 1 ケースの制限時間（秒） */
    timeLimitSec: number;
    drafts: DraftStore;
    /** エディタの読み上げ名 */
    editorLabel?: string;
    /** 区画の見出しの深さ（例題は h3、類題は h4） */
    headingLevel?: 3 | 4;
  }

  let {
    kind,
    target,
    draftKey,
    starter,
    cases,
    solution,
    hints,
    timeLimitSec,
    drafts,
    editorLabel = 'Swift のコード',
    headingLevel = 3,
  }: Props = $props();

  const h = $derived(`h${headingLevel}`);
  const hSub = $derived(`h${headingLevel + 1}`);

  const uid = $props.id();
  const SAVE_DELAY_MS = 1000;

  // svelte-ignore state_referenced_locally
  let code = $state(drafts.load(draftKey) ?? starter);
  // svelte-ignore state_referenced_locally
  let stdin = $state(cases.find((c) => c.sample === true)?.in ?? cases[0]?.in ?? '');
  let trial = $state<RunResponse | null>(null);
  let judge = $state<JudgeResult | null>(null);
  /** この画面を開いてから一度でも全ケース AC になったか（保存しない） */
  let acSeen = $state(false);
  let hintsOpen = $state(0);
  let solutionRevealed = $state(false);
  let mode = $state<'grade' | 'run' | null>(null);
  let lastAction: 'grade' | 'run' = 'grade';

  const run = new RunState();

  // ---- 書きかけの保存（§8。編集が止まって 1 秒後。離れるときは待たずに書く） ----
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;

  function flush() {
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = null;
    if (!pending) return;
    pending = false;
    drafts.save(draftKey, code, starter);
  }

  function onchange() {
    pending = true;
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = setTimeout(flush, SAVE_DELAY_MS);
  }

  function resetStarter() {
    if (saveTimer !== null) clearTimeout(saveTimer);
    saveTimer = null;
    pending = false;
    code = starter;
    drafts.clear(draftKey);
  }

  onDestroy(() => {
    flush();
    run.dispose();
  });

  // リロード・タブを閉じる・バックグラウンド化でも未保存分を書く（1 秒の遅延を待たない）
  function flushIfHidden() {
    if (document.visibilityState === 'hidden') flush();
  }

  // ---- 実行と採点 ----
  async function grade() {
    if (run.running || code.trim() === '') return;
    mode = 'grade';
    lastAction = 'grade';
    const snapshot = code;
    const res = await run.start((hooks) => gradeProblemCode(target, snapshot, cases, hooks));
    mode = null;
    if (!res) return;
    // 制限時間（time_limit_sec / exerciseTimeLimit）を超えたケースは TLE
    judge = applyTimeLimit(res, timeLimitSec);
    trial = null;
    if (judge.overall === 'AC') acSeen = true;
  }

  async function tryRun() {
    if (run.running || code.trim() === '') return;
    mode = 'run';
    lastAction = 'run';
    trial = null;
    const res = await run.start((hooks) => runProblemCode(target, code, stdin, hooks));
    mode = null;
    if (res) trial = res;
  }

  function retry() {
    if (lastAction === 'run') void tryRun();
    else void grade();
  }

  const summary = $derived.by(() => {
    if (!judge) return null;
    const r = writeAcResult(judge, cases);
    if (judge.overall !== 'TLE' || r.caseIndex === undefined) return r;
    // write-ac の文言は sandboxd の 3 秒。問題では制限時間で書く
    const n = judge.cases.length;
    return {
      ...r,
      message: `${caseLabel(cases, r.caseIndex)} が ${timeLimitSec} 秒以内に終わりませんでした。${n} ケース中 ${judge.acCount} ケースで一致しました。`,
    };
  });
  const failure = $derived(judge ? firstFailure(judge) : undefined);
  const ceLines = $derived.by(() => {
    const out = judge?.overall === 'CE' ? judge.compileOutput : '';
    const lines: { line: number; message: string }[] = [];
    for (const m of out.matchAll(/^main\.swift:(\d+):\d+: error: (.*)$/gm)) {
      lines.push({ line: Number(m[1]), message: m[2] ?? '' });
    }
    return lines;
  });
  const trialRun = $derived(trial?.runs?.[0]);
  const trialCe = $derived(
    trial && trial.compile?.ok === false ? (trial.compile.output ?? '') : null,
  );
  const canRun = $derived(!run.running && code.trim() !== '');
  const solutionShown = $derived(acSeen || solutionRevealed);
  const limitLabel = $derived(`${timeLimitSec} 秒`);

  function openHint(n: number) {
    if (n === hintsOpen + 1) hintsOpen = n;
  }
</script>

<svelte:window onpagehide={flush} onvisibilitychange={flushIfHidden} />

<div class="bench" data-testid="{kind}-workbench">
  <div class="editor-head">
    <svelte:element this={h} class="sub-title" id="{uid}-code">コードを書く</svelte:element>
    <button
      class="btn btn-quiet btn-sm"
      type="button"
      data-testid="{kind}-reset-starter"
      disabled={run.running || code === starter}
      onclick={resetStarter}>ひな形に戻す</button
    >
  </div>
  <div
    data-testid="{kind}-editor"
    data-exercise={kind === 'exercise' ? target.exerciseId : undefined}
  >
    <CodeEditor
      bind:value={code}
      id="{uid}-editor"
      label={editorLabel}
      readonly={run.running}
      minLines={8}
      errorLines={ceLines}
      {onchange}
      onsubmit={() => void grade()}
    />
  </div>

  <div class="trial">
    <label class="sub" for="{uid}-stdin">標準入力（「実行」で使う）</label>
    <textarea
      id="{uid}-stdin"
      class="stdin"
      rows="3"
      spellcheck="false"
      autocapitalize="off"
      autocomplete="off"
      data-testid="{kind}-stdin"
      bind:value={stdin}></textarea>
    <div class="actions">
      <button
        class="btn"
        type="button"
        data-testid="{kind}-run"
        disabled={!canRun}
        aria-busy={run.running && mode === 'run' ? 'true' : undefined}
        onclick={() => void tryRun()}>{run.running && mode === 'run' ? '実行中' : '実行'}</button
      >
      <button
        class="btn btn-primary"
        type="button"
        data-testid="{kind}-judge"
        disabled={!canRun}
        aria-busy={run.running && mode === 'grade' ? 'true' : undefined}
        onclick={() => void grade()}
        >{run.running && mode === 'grade' ? '判定中' : 'ジャッジ'}
        <kbd>{SUBMIT_CHORD_LABEL}</kbd></button
      >
      <span class="note"
        >採点は全 {cases.length} ケース。制限時間は 1 ケース {limitLabel}です。</span
      >
    </div>
  </div>

  <RunStatus {run} busyLabel={mode === 'run' ? '実行中' : '判定中'} onretry={retry} />

  {#if trial}
    <div class="result" data-testid="{kind}-trial-result">
      {#if trialCe !== null}
        <svelte:element this={hSub} class="sub">コンパイラの出力</svelte:element>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="コンパイラの出力">{trialCe}</pre>
      {:else if trialRun}
        {#if trialRun.timedOut}
          <p class="note">
            <VerdictBadge verdict="TLE" size="sm" /> 3 秒以内に終わりませんでした。
          </p>
        {:else if trialRun.exitCode !== 0}
          <p class="note"><VerdictBadge verdict="RE" size="sm" /> 実行の途中で止まりました。</p>
        {/if}
        <OutputPanel
          mode="plain"
          stdout={trialRun.stdout}
          stderr={trialRun.stderr}
          durationMs={trialRun.durationMs}
        />
      {/if}
    </div>
  {/if}

  <div class="result" role="status" data-testid="{kind}-judge-status">
    {#if judge && summary}
      <p class="summary">
        <VerdictBadge verdict={judge.overall} />
        <span>{summary.message}</span>
      </p>
    {/if}
  </div>

  {#if judge}
    <ol class="cases" aria-label="ケースごとの判定" data-testid="{kind}-case-verdicts">
      {#each judge.cases as c (c.index)}
        <li data-verdict={c.verdict}>
          <VerdictBadge verdict={c.verdict} size="sm" caseLabel={caseLabel(cases, c.index)} />
          <span>{caseLabel(cases, c.index)}</span>
          {#if c.durationMs !== null}
            <span class="num ms">{c.durationMs} ms</span>
            {#if overLimit(c.durationMs, timeLimitSec)}
              <span class="over">（制限 {limitLabel}を超えています）</span>
            {/if}
          {/if}
        </li>
      {/each}
    </ol>
    {#if judge.overall === 'CE'}
      {@const line = compileErrorLine(judge.compileOutput)}
      <svelte:element this={hSub} class="sub"
        >コンパイラの出力{line ? `（${line} 行目）` : ''}</svelte:element
      >
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="コンパイラの出力">{judge.compileOutput}</pre>
    {:else if failure?.verdict === 'WA'}
      <OutputPanel
        mode="diff"
        expected={cases[failure.index]?.out ?? ''}
        actual={failure.stdout}
        stderr={failure.stderr}
        {...failure.durationMs !== null ? { durationMs: failure.durationMs } : {}}
      />
    {:else if failure}
      <OutputPanel
        mode="plain"
        stdout={failure.stdout}
        stderr={failure.stderr}
        {...failure.durationMs !== null ? { durationMs: failure.durationMs } : {}}
      />
    {/if}
  {/if}

  <section class="hints" aria-labelledby="{uid}-hints">
    <svelte:element this={h} class="sub-title" id="{uid}-hints">ヒント</svelte:element>
    <ol class="hint-list">
      {#each hints as hint (hint.n)}
        {@const open = hintsOpen >= hint.n}
        <li>
          <button
            class="btn btn-sm hint-toggle"
            type="button"
            data-testid="{kind}-hint"
            data-n={hint.n}
            aria-expanded={open}
            aria-controls="{uid}-hint-{hint.n}"
            disabled={!open && hint.n !== hintsOpen + 1}
            onclick={() => openHint(hint.n)}
            >ヒント {hint.n}<span class="visually-hidden"> / {hints.length}</span></button
          >
          <p class="hint-text" id="{uid}-hint-{hint.n}" hidden={!open}>{hint.text}</p>
        </li>
      {/each}
    </ol>
  </section>

  {#if solutionShown}
    <section class="solution" aria-labelledby="{uid}-sol" data-testid="{kind}-solution">
      <svelte:element this={h} class="sub-title" id="{uid}-sol">模範解答</svelte:element>
      <CodeBlock code={solution.replace(/\n$/, '')} label="模範解答" />
    </section>
  {:else if hintsOpen >= hints.length && hints.length > 0}
    <div>
      <button
        class="btn btn-sm"
        type="button"
        data-testid="{kind}-solution-reveal"
        onclick={() => (solutionRevealed = true)}>模範解答を見る</button
      >
    </div>
  {/if}
</div>

<style>
  .bench {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .editor-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .sub-title {
    font-size: var(--fs-md);
  }
  .sub {
    display: block;
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .trial,
  .result {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }
  .stdin {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-2) var(--space-3);
    flex-wrap: wrap;
  }
  .actions kbd {
    margin-left: var(--space-1);
  }
  .note {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-3);
  }
  .cases {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .cases li {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-2);
    font-size: var(--fs-sm);
  }
  .ms {
    color: var(--text-soft);
  }
  .over {
    color: var(--warn-text);
    background: var(--warn-bg);
    padding: 0 var(--space-1);
    border-radius: var(--radius-xs);
  }
  .hints {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .hint-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .hint-text {
    margin: var(--space-2) 0 0;
    padding: var(--space-2) var(--space-3);
    border-left: 3px solid var(--info-text);
    background: var(--info-bg);
    color: var(--text);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  }
  .hint-toggle[aria-expanded='true'] {
    font-weight: var(--fw-semibold);
  }
  .solution {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }
</style>
